import { useCallback, useState } from 'react'
import type { MeetingInviteEvent } from '@ai-meeting/shared'
import { useStore } from '@/store/useStore'

export interface UseJoinMeetingInviteReturn {
  /**
   * @param invite The room to join.
   * @param onAccepted Runs once the room is confirmed live and before the create form opens —
   *   where the caller retires its own copy of the invite, or leaves the screen the form is not on.
   */
  join: (invite: MeetingInviteEvent, onAccepted?: () => void) => Promise<void>
  /** The meeting whose room is being checked right now, or null. */
  checkingId: string | null
  /** Meetings the check found had already ended, so each place can say so where the row was. */
  endedIds: string[]
  /** Drops a stale "already ended" flag after dismiss or a fresh re-invite. */
  clearEnded: (meetingId: string) => void
}

/**
 * The one action an invite offers, and the check that has to come first.
 *
 * Join does not open the sharer's meeting — that one is theirs, and the API answers a recipient's
 * writes with 403. It opens the ordinary create form carrying the room key, so the recipient picks
 * their own audio capture and ends up with a meeting of their own in the same room.
 *
 * The room is re-checked at the press rather than trusted. Nothing pushed can retire an invite:
 * the sharer's machine is the only one that knows their meeting stopped, and an abandoned meeting
 * never announces anything. Acting on a stale one creates a meeting that records alone in an empty
 * room and pays for its own translation — the exact cost this feature exists to avoid.
 *
 * Shared by the notification and the Ongoing Meetings row because that rule is the whole point of
 * the feature, and two copies of it would be two chances to lose it.
 *
 * @param confirmStillLive Asks the server whether that room is joinable, from `useMeetingInvites`.
 */
export function useJoinMeetingInvite(
  confirmStillLive: (meetingId: string) => Promise<boolean>,
): UseJoinMeetingInviteReturn {
  const acceptMeetingInvite = useStore((state) => state.acceptMeetingInvite)
  const [checkingId, setCheckingId] = useState<string | null>(null)
  const [endedIds, setEndedIds] = useState<string[]>([])

  const clearEnded = useCallback((meetingId: string) => {
    setEndedIds((current) => current.filter((id) => id !== meetingId))
  }, [])

  const join = useCallback(
    async (invite: MeetingInviteEvent, onAccepted?: () => void) => {
      // A share with no room is an ordinary History share (BRAI-417): the meeting simply appears
      // in the recipient's list, and there is nothing to join.
      if (!invite.sharedRoomId) return

      setCheckingId(invite.meetingId)
      try {
        if (!(await confirmStillLive(invite.meetingId))) {
          setEndedIds((current) =>
            current.includes(invite.meetingId)
              ? current
              : [...current, invite.meetingId],
          )
          return
        }
        setEndedIds((current) => current.filter((id) => id !== invite.meetingId))
        onAccepted?.()
        acceptMeetingInvite(invite)
      } finally {
        setCheckingId(null)
      }
    },
    [acceptMeetingInvite, confirmStillLive],
  )

  return { join, checkingId, endedIds, clearEnded }
}
